import {
  HEALTH_CARD_PHOTO_LIMITS,
  HEALTH_CARD_PHOTO_MEDIA_TYPE,
  inspectHealthCardJpeg,
  stripJpegMetadata,
} from "@emr/contracts";

/**
 * Health card photos taken at the desk (PA-13): the browser decodes the picture (turned upright
 * from its orientation tag), draws it no larger than HEALTH_CARD_PHOTO_LIMITS.clientLongEdgePx
 * on a canvas, re-encodes it as a JPEG, drops any metadata the encoder wrote and checks the
 * result with the same rules the API applies. The camera's original file (with its Exif, GPS and
 * thumbnails) never leaves the browser. Capture only: nothing reads the card.
 */
export interface PreparedHealthCardPhoto {
  /** The JPEG that is sent, without metadata. */
  readonly bytes: Uint8Array<ArrayBuffer>;
  readonly width: number;
  readonly height: number;
}

/** Why a picture could not be prepared, in words for the photo's row. */
export type HealthCardPhotoProblem = "unreadable" | "too_small" | "too_large";

export class HealthCardPhotoError extends Error {
  public readonly problem: HealthCardPhotoProblem;

  public constructor(problem: HealthCardPhotoProblem) {
    super(HEALTH_CARD_PHOTO_PROBLEM_TEXT[problem]);
    this.name = "HealthCardPhotoError";
    this.problem = problem;
  }
}

export const HEALTH_CARD_PHOTO_PROBLEM_TEXT: Readonly<Record<HealthCardPhotoProblem, string>> = {
  unreadable: "This picture couldn't be read. Take it again, or type the card details.",
  too_small: "This picture is too small to read. Take it closer, or type the card details.",
  too_large: "This picture is too large. Take it again, or type the card details.",
};

/** Whether this browser can prepare a photo; without it the photo controls are not offered. */
export function healthCardPhotoCaptureSupported(): boolean {
  return (
    typeof globalThis.createImageBitmap === "function" &&
    typeof HTMLCanvasElement !== "undefined" &&
    typeof HTMLCanvasElement.prototype.toBlob === "function" &&
    typeof URL.createObjectURL === "function"
  );
}

function canvasJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      resolve,
      HEALTH_CARD_PHOTO_MEDIA_TYPE,
      HEALTH_CARD_PHOTO_LIMITS.clientJpegQuality,
    );
  });
}

/** Downscales, re-encodes and strips a picture the desk took or chose. */
export async function prepareHealthCardPhoto(file: Blob): Promise<PreparedHealthCardPhoto> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new HealthCardPhotoError("unreadable");
  }
  const canvas = document.createElement("canvas");
  try {
    const { clientLongEdgePx, minEdgePx } = HEALTH_CARD_PHOTO_LIMITS;
    const scale = Math.min(1, clientLongEdgePx / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    if (Math.min(width, height) < minEdgePx) throw new HealthCardPhotoError("too_small");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (context === null) throw new HealthCardPhotoError("unreadable");
    context.drawImage(bitmap, 0, 0, width, height);
    const encoded = await canvasJpeg(canvas);
    if (encoded === null) throw new HealthCardPhotoError("unreadable");
    const stripped = stripJpegMetadata(new Uint8Array(await encoded.arrayBuffer()));
    if (stripped === null) throw new HealthCardPhotoError("unreadable");
    const inspection = inspectHealthCardJpeg(stripped);
    if (!inspection.ok) {
      throw new HealthCardPhotoError(
        inspection.issue === "too_large"
          ? "too_large"
          : inspection.issue === "dimensions"
            ? "too_small"
            : "unreadable",
      );
    }
    return { bytes: stripped.slice(), width: inspection.width, height: inspection.height };
  } finally {
    bitmap.close();
    // Free the decoded pixels now rather than when the canvas is collected.
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** A picture's problem in words, whatever failed. */
export function healthCardPhotoProblemText(error: unknown): string {
  return error instanceof HealthCardPhotoError
    ? error.message
    : HEALTH_CARD_PHOTO_PROBLEM_TEXT.unreadable;
}

/** How long a registration waits for its photo before cancelling it and moving on. */
export const HEALTH_CARD_PHOTO_UPLOAD_WAIT_MS = 5_000;

/** What became of a photo sent after a registration. */
export type HealthCardPhotoUploadOutcome = "saved" | "not_saved";

/**
 * Sends a registration's photo once the chart exists, waiting at most `waitMs`. At the deadline
 * the request is aborted so navigation leaves no detached upload. Failure never undoes the
 * registration; staff can check the chart's Details and add its photo there.
 */
export async function sendHealthCardPhotoWithin(
  upload: (signal: AbortSignal) => Promise<unknown>,
  waitMs: number = HEALTH_CARD_PHOTO_UPLOAD_WAIT_MS,
): Promise<HealthCardPhotoUploadOutcome> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settled = (async (): Promise<HealthCardPhotoUploadOutcome> => {
    try {
      await upload(controller.signal);
      return "saved";
    } catch {
      return "not_saved";
    }
  })();
  const waited = new Promise<HealthCardPhotoUploadOutcome>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve("not_saved");
    }, waitMs);
  });
  try {
    return await Promise.race([settled, waited]);
  } finally {
    clearTimeout(timer);
  }
}
